<div class="relative overflow-hidden bg-bg-base">
  <!-- Background Decorations -->
  <div class="pointer-events-none absolute inset-0 overflow-hidden">
    <div class="absolute -right-40 -top-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl"></div>
    <div class="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary-deep/5 blur-3xl"></div>
  </div>

  <section class="relative py-20 lg:py-28" id="user-categories">
    <div class="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <!-- Header Section -->
      <div class="mb-16 animate-in zoom-in text-center duration-700 fade-in">
        <div
          class="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2"
        >
          <i class="fa-solid fa-users text-primary"></i>
          <span class="text-sm font-medium text-primary">{{
            'home.whoAreYou.badge' | translate
          }}</span>
        </div>
        <h2
          class="mb-6 bg-primary bg-clip-text text-4xl font-bold text-transparent md:text-5xl leading-normal"
        >
          {{ 'home.whoAreYou.title' | translate }}
        </h2>
        <p class="mx-auto max-w-2xl text-lg leading-relaxed text-text-secondary">
          {{ 'home.whoAreYou.subtitle' | translate }}
        </p>
      </div>

      <!-- User Categories Grid -->
      <div
        class="mb-16 grid grid-cols-1 gap-6 animate-in slide-in-from-bottom duration-700 delay-200 fade-in md:grid-cols-2 lg:gap-8"
      >
        @for (category of userCategories; track category.title) {
          <app-user-category-card [category]="category"></app-user-category-card>
        }
      </div>

      <!-- Trusted By Section -->
      <div class="mt-20 animate-in slide-in-from-bottom text-center duration-700 delay-400 fade-in">
        <p class="mb-8 text-sm font-semibold uppercase tracking-[0.16em] text-text-muted">
          {{ 'home.whoAreYou.trustedBy' | translate }}
        </p>
        <div
          class="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8"
        >
          <div
            class="group surface-card px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-deep ring-1 ring-primary/30"
              >
                <i class="fa-solid fa-building text-white text-lg"></i>
              </div>
              <span
                class="font-semibold text-text-primary transition-colors group-hover:text-primary"
                >{{ 'home.whoAreYou.companies.realEstate' | translate }}</span
              >
            </div>
          </div>

          <div
            class="group surface-card px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-deep ring-1 ring-primary/30"
              >
                <i class="fa-solid fa-city text-white text-lg"></i>
              </div>
              <span
                class="font-semibold text-text-primary transition-colors group-hover:text-primary"
                >{{ 'home.whoAreYou.companies.developers' | translate }}</span
              >
            </div>
          </div>

          <div
            class="group surface-card px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-deep ring-1 ring-primary/30"
              >
                <i class="fa-solid fa-handshake text-white text-lg"></i>
              </div>
              <span
                class="font-semibold text-text-primary transition-colors group-hover:text-primary"
                >{{ 'home.whoAreYou.companies.agencies' | translate }}</span
              >
            </div>
          </div>

          <div
            class="group surface-card px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-deep ring-1 ring-primary/30"
              >
                <i class="fa-solid fa-hammer text-white text-lg"></i>
              </div>
              <span
                class="font-semibold text-text-primary transition-colors group-hover:text-primary"
                >{{ 'home.whoAreYou.companies.construction' | translate }}</span
              >
            </div>
          </div>
        </div>

        <!-- Stats Section -->
        <div class="mx-auto mt-16 grid max-w-4xl grid-cols-2 gap-6 md:grid-cols-4">
          <div
            class="surface-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="mb-2 text-3xl font-bold text-primary">500+</div>
            <div class="text-sm text-text-secondary">
              {{ 'home.whoAreYou.stats.activeUsers' | translate }}
            </div>
          </div>
          <div
            class="surface-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="mb-2 text-3xl font-bold text-primary">1000+</div>
            <div class="text-sm text-text-secondary">
              {{ 'home.whoAreYou.stats.properties' | translate }}
            </div>
          </div>
          <div
            class="surface-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="mb-2 text-3xl font-bold text-primary">50+</div>
            <div class="text-sm text-text-secondary">
              {{ 'home.whoAreYou.stats.partners' | translate }}
            </div>
          </div>
          <div
            class="surface-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/50"
          >
            <div class="mb-2 text-3xl font-bold text-primary">98%</div>
            <div class="text-sm text-text-secondary">
              {{ 'home.whoAreYou.stats.satisfaction' | translate }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
